import type { IconType } from "@clipboard-health/ui-components";
import { Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CssBaseline, Stack, ThemeProvider } from "@mui/material";
// Need to import the route to the next path
// eslint-disable-next-line no-restricted-imports
import { STAGES } from "@src/app/onboarding/model";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useHistory } from "react-router-dom";

import { OnboardingHeader } from "../Header";

export const INTERESTED_WORK_TYPE_STORAGE_KEY = "interested-work-type";

export enum WorkTypeOption {
  PERMANENT_PLACEMENTS = "PERMANENT_PLACEMENTS",
  FLEX = "FLEX",
  PERMANENT_AND_FLEX = "PERMANENT_AND_FLEX",
}

interface WorkTypeConfig {
  value: WorkTypeOption;
  iconType: IconType;
  title: string;
  description: string;
}

/**
 * @deprecated. We are working on deprecating this page. Once we have have new signing up flow, this page will be removed.
 *
 * New page: src/appV2/redesign/Onboarding/OnboardingFlowContainer/InterestedWorkTypeStep/InterestedWorkTypeStepContent.tsx
 */
export function InterestedWorkType() {
  const [selectedOption, setSelectedOption] = useLocalStorage<WorkTypeOption | undefined>(
    INTERESTED_WORK_TYPE_STORAGE_KEY,
    undefined
  );

  const history = useHistory();

  const isOptionSelected = selectedOption !== undefined;

  const workTypeConfigs: WorkTypeConfig[] = [
    {
      value: WorkTypeOption.PERMANENT_PLACEMENTS,
      iconType: "briefcase",
      title: "Finding a job",
      description: "Interview for a permanent position directly with the facility",
    },
    {
      value: WorkTypeOption.FLEX,
      iconType: "calendar-navigation",
      title: "Picking up shifts",
      description: "Book individual shifts as an independent contractor",
    },
    {
      value: WorkTypeOption.PERMANENT_AND_FLEX,
      iconType: "clipboard",
      title: "Both",
      description: "Get hired for a permanent job and/or work shifts as an independent contractor",
    },
  ];

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_INTERESTED_WORK_TYPE_PAGE_VIEWED);

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper variant="tertiary">
        <OnboardingHeader
          title="What kind of work are you interested in?"
          fallbackGoBackPath={`${RootPaths.HOME}/onboarding`}
        />

        <PageContent>
          <Box sx={{ py: 12 }}>
            <Stack direction="column" spacing={8}>
              <Stack direction="column" spacing={4}>
                {workTypeConfigs.map((config) => (
                  <SelectableBox
                    key={config.value}
                    startIconType={config.iconType}
                    variant="secondary"
                    isSelected={selectedOption === config.value}
                    sx={{
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "start",
                      background: (theme) =>
                        selectedOption === config.value
                          ? theme.palette.background.tertiary
                          : undefined,
                    }}
                    onToggleSelect={() => {
                      setSelectedOption(config.value);
                    }}
                  >
                    <Stack direction="column" spacing={2}>
                      <Text semibold variant="h6">
                        {config.title}
                      </Text>
                      <Text variant="body2">{config.description}</Text>
                    </Stack>
                  </SelectableBox>
                ))}
              </Stack>
            </Stack>
          </Box>
          <Stack
            position="absolute"
            bottom={(theme) => theme.spacing(5)}
            left={0}
            justifyContent="center"
            alignItems="center"
            width="100%"
            sx={{ px: 5 }}
          >
            <Button
              fullWidth
              variant="contained"
              disabled={!isOptionSelected}
              size="large"
              onClick={() => {
                const nextPath = STAGES.interestedWorkType.nextStagePath;
                logEvent(APP_V2_USER_EVENTS.ONBOARDING_INTERESTED_WORK_TYPE_SELECTED, {
                  selectedOption,
                });
                history.push(String(nextPath));
              }}
            >
              Continue
            </Button>
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
